import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Asset, RelatedAsset } from '@app/core/model/entities/asset/asset';
import { ManualWorkflowViewEnum } from '@app/core/model/entities/workflow/workflow';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { AssetModalService } from '@app/features/main/views/assets/modals/asset-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { from, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  templateUrl: './asset-header.component.html',
  styleUrls: ['./asset-header.component.scss'],
  providers: [AssetModalService],
  standalone: false
})
export class AssetHeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly assetsService = inject(AssetsService);
  private readonly assetModalService = inject(AssetModalService);
  private readonly appManager = inject(AppManager);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly activationEndService = inject(ActivationEndService);
  private readonly translate = inject(TranslateService);
  private readonly titleService = inject(Title);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  public Permission = PermissionEnum;
  public permissionsForEdition = [PermissionEnum.EDIT_ASSET];
  protected readonly ManualWorkflowViewEnum = ManualWorkflowViewEnum;
  protected readonly EntityType = EntityTypeEnum;

  public asset: Asset;

  public selectAssetFormControl: FormControl<RelatedAsset>;

  public ngOnInit(): void {
    this.asset = this.appManager.currentEntity as Asset;

    // Update Asset as it is updated outside the header
    this.assetsService.assetUpdated$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updatedAsset => {
        this.asset = updatedAsset;
      });

    this.selectAssetFormControl = this.fb.control(void 0);

    this.activationEndService.pushOptionalParams({
      'TITLE.ASSET_TYPE_IDENTIFIER': {
        identifier: this.asset.identifier.toString(),
        type: this.asset.computedProperties.typeName
      }
    });
    this.titleService.setTitle(this.appManager.currentEntity['name'] + ' - myA');

    // Navigate to the selected asset sheet
    this.selectAssetFormControl.valueChanges.pipe(
      filter(asset => !!asset),
      switchMap((relatedAsset: RelatedAsset) => {
        const currentTabName = this.router.url.split('/').pop();
        return from(this.assetsService.navigateToAssetSheet(relatedAsset.id, currentTabName));
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  /**
   * Show confirmation dialog then delete the Asset.
   */
  public deleteAsset(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.HEADER,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.ASSET,
      this.asset.id
    );
    this.assetModalService.openDeleteAssetsDialog([this.asset])
      .pipe(
        filter(result => !!result),
        switchMap(() => {
          this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.ASSET_DELETED'));
          return this.router.navigate([
            'organization',
            this.appManager.currentOrganization.id,
            'assets'
          ], {replaceUrl: true});
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
